import React, { useState, useEffect } from 'react';
import { commissionService } from '../../services/api';
import { Plus, Search, Edit2, Trash2, X, Coins, UserCircle, Briefcase, IndianRupee, CheckCircle, AlertCircle, Clock } from 'lucide-react';

function CommissionsManagement() {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCommission, setEditingCommission] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    recipient_id: '',
    recipient_type: 'dealer',
    amount: '',
    type: 'sales',
    description: '',
    status: 'pending'
  });

  useEffect(() => {
    loadCommissions();
  }, []);

  const loadCommissions = async () => {
    setLoading(true);
    try {
      const data = await commissionService.index();
      setCommissions(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error('Error loading commissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCommission) {
        await commissionService.update(editingCommission.id, formData);
      } else {
        await commissionService.store(formData);
      }
      closeModal();
      loadCommissions();
    } catch (error) {
      alert('Error saving commission: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (commission) => {
    setEditingCommission(commission);
    setFormData({
      recipient_id: commission.recipient_id || '',
      recipient_type: commission.recipient_type || 'dealer',
      amount: commission.amount || '',
      type: commission.type || 'sales',
      description: commission.description || '',
      status: commission.status || 'pending'
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this commission entry?')) {
      try {
        await commissionService.destroy(id);
        loadCommissions();
      } catch (error) {
        console.error('Error deleting commission:', error);
      }
    }
  };

  const handleAction = async (id, action) => {
    try {
      if (action === 'approve') await commissionService.approve(id);
      if (action === 'reject') await commissionService.reject(id);
      if (action === 'pay') await commissionService.pay(id);
      loadCommissions();
    } catch (error) {
      alert('Error performing action: ' + (error.response?.data?.message || error.message));
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCommission(null);
    setFormData({ recipient_id: '', recipient_type: 'dealer', amount: '', type: 'sales', description: '', status: 'pending' });
  };

  const filteredCommissions = commissions.filter(c => 
    c.recipient_id?.toString().includes(searchQuery) ||
    c.recipient_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.type?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusStyle = (status) => {
    switch (status) {
      case 'paid': return 'bg-red-500/10 text-red-500';
      case 'approved': return 'bg-blue-500/10 text-blue-500';
      case 'rejected': return 'bg-red-500/10 text-red-500';
      default: return 'bg-amber-500/10 text-amber-500';
    }
  };

  if (loading) return <div className="text-slate-400 animate-pulse">Calculating network earnings...</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-100">Commission Ledger</h2>
          <p className="text-sm text-slate-500 mt-1">Track and process payouts for partners and agents.</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center justify-center gap-2 bg-red-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-red-500 transition-all shadow-lg shadow-red-900/20"
        >
          <Plus size={18} />
          Log Commission
        </button>
      </div>

      <div className="bg-slate-900 rounded-3xl border border-slate-800 overflow-hidden">
        <div className="p-4 border-b border-slate-800 bg-slate-900/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            <input
              type="text"
              placeholder="Search by recipient ID, type or category..."
              className="w-full bg-slate-950 border border-slate-800 rounded-xl py-2.5 pl-10 pr-4 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-950/50">
                <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-slate-500">Recipient</th>
                <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-slate-500">Amount</th>
                <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-slate-500">Category</th>
                <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-slate-500">Status</th>
                <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-slate-500 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {filteredCommissions.map((c) => (
                <tr key={c.id} className="hover:bg-slate-800/30 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 border border-slate-700">
                        {c.recipient_type === 'dealer' ? <Briefcase size={18} /> : <UserCircle size={18} />}
                      </div>
                      <div>
                        <div className="font-bold text-slate-200">ID: #{c.recipient_id}</div>
                        <div className="text-[10px] text-red-500 uppercase tracking-widest font-black">
                          {c.recipient_type}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-100 font-bold flex items-center gap-1 font-mono">
                      <IndianRupee size={14} className="text-red-500" />
                      {parseFloat(c.amount).toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-xs uppercase font-black tracking-widest text-slate-400 bg-slate-950 px-2 py-1 rounded inline-block border border-slate-800">
                      {c.type}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase ${getStatusStyle(c.status)}`}>
                      {c.status === 'paid' ? <CheckCircle size={10} /> : c.status === 'rejected' ? <AlertCircle size={10} /> : <Clock size={10} />}
                      {c.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      {c.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleAction(c.id, 'approve')}
                            className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-all"
                            title="Approve Payout"
                          >
                            <CheckCircle size={16} />
                          </button>
                          <button
                            onClick={() => handleAction(c.id, 'reject')}
                            className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-all"
                            title="Reject Payout"
                          >
                            <X size={16} />
                          </button>
                        </>
                      )}
                      {c.status === 'approved' && (
                        <button
                          onClick={() => handleAction(c.id, 'pay')}
                          className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-all font-black text-[10px] uppercase tracking-widest px-3 border border-red-500/30"
                        >
                          Mark Paid
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(c)}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all"
                        title="Edit Entry"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(c.id)}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all"
                        title="Delete Entry"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredCommissions.length === 0 && (
            <div className="text-center py-20">
              <div className="bg-slate-800 w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-4 text-slate-600">
                <Coins size={32} />
              </div>
              <p className="text-slate-500 font-medium">No commission entries found.</p>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="px-6 py-4 border-b border-slate-800 flex justify-between items-center bg-slate-900/50">
              <h3 className="text-lg font-bold text-slate-100">
                {editingCommission ? 'Edit Commission Details' : 'Log New Commission'}
              </h3>
              <button onClick={closeModal} className="text-slate-500 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Recipient ID</label>
                  <input
                    type="text"
                    required
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
                    value={formData.recipient_id}
                    onChange={(e) => setFormData({ ...formData, recipient_id: e.target.value })}
                    placeholder="Enter ID / Reference"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Recipient Type</label>
                  <select
                    required
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors appearance-none"
                    value={formData.recipient_type}
                    onChange={(e) => setFormData({ ...formData, recipient_type: e.target.value })}
                  >
                    <option value="dealer">Dealer</option>
                    <option value="distributor">Distributor</option>
                    <option value="sales_agent">Sales Agent</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Payout Amount (₹)</label>
                  <div className="relative">
                    <IndianRupee size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                      type="number"
                      step="0.01"
                      required
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 pl-10 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors font-mono"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Category</label>
                  <select
                    required
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors appearance-none"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    <option value="sales">Sales Commission</option>
                    <option value="referral">Referral Bonus</option>
                    <option value="performance">Performance Bonus</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Transaction Memo</label>
                <textarea
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors resize-none"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Details about this payout..."
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Verification Status</label>
                <div className="flex flex-wrap gap-2">
                  {['pending', 'approved', 'paid', 'rejected'].map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setFormData({ ...formData, status: s })}
                      className={`flex-1 min-w-[100px] py-2.5 rounded-xl text-[10px] uppercase font-black tracking-widest border transition-all ${
                        formData.status === s 
                          ? getStatusStyle(s) + ' border-current'
                          : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-700'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-2.5 rounded-xl text-sm font-semibold text-slate-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-8 py-2.5 bg-red-600 text-white rounded-xl font-bold hover:bg-red-500 transition-all shadow-lg shadow-red-900/20"
                >
                  {editingCommission ? 'Update Ledger' : 'Confirm Entry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CommissionsManagement;

